import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFamilyMembers, useRecipes, useSwipes, useMealPlans, useHouseholdSettings, useAllFeedback } from '../hooks/useSupabase';
import { useTonight } from '../hooks/useTonight';
import { RecipeThumb } from './RecipeImage';
import { buildActivityFeed } from '../lib/activityFeed';
import { formatMealTime, getStartByTime } from '../lib/mealTimeUtils';
import Avatar from './Avatar';
import { LoadingSpinner, EmptyState, ErrorState } from './ui/StateViews';
import RecipeDetail from './RecipeDetail';
import HouseholdFeed from './HouseholdFeed';

const DAY_ABBRS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return { text: 'Good morning', sub: 'Ready to pick some meals?' };
  if (h < 18) return { text: 'Good afternoon', sub: 'Ready to plan the week?' };
  return { text: 'Good evening', sub: "How was tonight's dinner?" };
}

function getTodayMeals(plan, recipes) {
  if (!plan?.plan) return null;
  const today = new Date();
  const dayAbbr = DAY_ABBRS[today.getDay()];
  const dayPlan = plan.plan[dayAbbr];
  if (!dayPlan) return null;

  const recipeMap = {};
  recipes.forEach(r => { recipeMap[r.id] = r; });

  const meals = [];
  for (const mealType of ['breakfast', 'lunch', 'dinner']) {
    const slot = dayPlan[mealType];
    if (!slot) continue;
    if (slot.shared && slot.recipeId) {
      const r = recipeMap[slot.recipeId];
      meals.push({ type: mealType, name: r?.name || 'Unknown recipe', emoji: r?.emoji, prep: r?.prep_time_min, cook: r?.cook_time_min, recipe: r });
    } else if (!slot.shared) {
      const adult = recipeMap[slot.adultRecipeId];
      const kid = recipeMap[slot.kidRecipeId];
      if (adult) meals.push({ type: mealType, name: adult.name, emoji: adult.emoji, label: 'Adults', prep: adult.prep_time_min, cook: adult.cook_time_min, recipe: adult });
      if (kid && kid.id !== adult?.id) meals.push({ type: mealType, name: kid.name, emoji: kid.emoji, label: 'Kids', prep: kid.prep_time_min, cook: kid.cook_time_min, recipe: kid });
    }
  }
  return meals.length > 0 ? meals : null;
}

function formatWeekOf(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function HomeScreen() {
  const { householdId, currentMember } = useAuth();
  const { members, loading: membersLoading, error: membersError, refetch: membersRefetch } = useFamilyMembers(householdId);
  const { recipes, loading: recipesLoading, error: recipesError, refetch: recipesRefetch } = useRecipes(householdId);
  const { swipes, rawSwipes, loading: swipesLoading, error: swipesError, refetch: swipesRefetch } = useSwipes(householdId);
  const { currentPlan, loading: planLoading, error: planError, refetch: planRefetch } = useMealPlans(householdId);
  const { mealTimes } = useHouseholdSettings(householdId);
  const { feedback, loading: feedbackLoading, error: feedbackError, refetch: feedbackRefetch } = useAllFeedback(householdId);

  const loading = membersLoading || recipesLoading || swipesLoading || planLoading || feedbackLoading;
  const error = membersError || recipesError || swipesError || planError || feedbackError;
  const refetchAll = () => { membersRefetch(); recipesRefetch(); swipesRefetch(); planRefetch(); feedbackRefetch(); };

  const memberSwipes = currentMember ? (swipes[currentMember.id] || {}) : {};
  const unratedCount = recipes.length - Object.keys(memberSwipes).length;
  const todayMeals = currentPlan ? getTodayMeals(currentPlan, recipes) : null;
  const { event: tonightEvent } = useTonight(householdId);
  const tonightRecipe = tonightEvent?.recipe_id ? recipes.find((r) => r.id === tonightEvent.recipe_id) : null;
  const tonightTitle = (tonightEvent && tonightEvent.status !== 'unplanned') ? (tonightRecipe?.name || tonightEvent.title) : null;
  // Prefer tonight's per-night target time (set on the Tonight card) over the household default.
  const tonightDinnerTime = (tonightEvent?.target_time ? tonightEvent.target_time.slice(0, 5) : null) || (mealTimes || {}).dinner;
  const tonightStartBy = tonightRecipe ? getStartByTime(tonightDinnerTime, tonightRecipe.prep_time_min, tonightRecipe.cook_time_min) : null;

  const firstName = currentMember?.name?.split(' ')[0] || 'there';
  const greeting = getGreeting();
  const [selectedRecipe, setSelectedRecipe] = useState(null);

  const activityItems = useMemo(
    () => buildActivityFeed({ members, recipes, rawSwipes, feedback }),
    [members, recipes, rawSwipes, feedback],
  );

  const handleFeedRecipeClick = (recipeId) => {
    const recipe = recipes.find(r => r.id === recipeId);
    if (recipe) setSelectedRecipe(recipe);
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorState message={error} onRetry={refetchAll} />;

  if (members.length === 0) {
    return (
      <EmptyState mascot title="Welcome to Suppertide" description="Add your first family member to get started." action="Build your family" to="/settings" />
    );
  }

  const isSunday = new Date().getDay() === 0;

  return (
    <div className="space-y-6 lg:space-y-8">
      {/* Hero — leads with the swipe-first gesture */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-surface via-panel to-canvas p-6 lg:p-8 text-ink shadow-lg shadow-glow-sm">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(132,204,22,0.12),transparent_55%)]" />
        <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-lime/15 blur-2xl" />
        <div className="relative z-10">
          <div className="lg:flex lg:items-start lg:justify-between">
            <div>
              <p className="font-body text-xs text-lime font-semibold mb-1">Suppertide</p>
              <h2 className="font-display text-2xl lg:text-3xl font-semibold leading-tight">{greeting.text}, {firstName}</h2>
              <p className="font-body text-sm text-ink/60 mt-1">{greeting.sub}</p>
            </div>
            <div className="flex items-center gap-3 mt-5 lg:mt-0">
              {members.map(m => (
                <div key={m.id} className="flex flex-col items-center gap-1">
                  <div className={`w-11 h-11 lg:w-12 lg:h-12 rounded-full flex items-center justify-center text-lg overflow-hidden transition-all duration-200 ${m.id === currentMember?.id ? 'bg-lime text-canvas ring-2 ring-lime/50 scale-110' : 'bg-ink/10 text-ink/80'}`}>
                    <Avatar url={m.avatar_url} fallback={m.avatar} name={m.name} />
                  </div>
                  <span className={`text-[10px] font-body transition-colors ${m.id === currentMember?.id ? 'text-lime font-semibold' : 'text-ink/50'}`}>{m.name.split(' ')[0]}</span>
                </div>
              ))}
            </div>
          </div>

          <Link
            to="/swipe"
            className="group mt-6 flex items-center justify-between gap-3 w-full px-5 py-4 rounded-2xl bg-lime-gradient text-canvas hover:brightness-110 transition-colors active:scale-[0.99] shadow-md shadow-glow"
          >
            <span className="flex items-center gap-3 min-w-0">
              <span className="shrink-0 w-11 h-11 rounded-xl bg-canvas/10 flex items-center justify-center">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M8 12h8" /><path d="M12 8v8" /></svg>
              </span>
              <span className="text-left min-w-0">
                <span className="block font-display text-base lg:text-lg font-semibold leading-tight">Rate a few dinners</span>
                <span className="block font-body text-xs font-semibold text-canvas/70 truncate">
                  {unratedCount > 0 ? `${unratedCount} new to rate` : 'All caught up'}
                </span>
              </span>
            </span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 w-5 h-5 group-hover:translate-x-0.5 transition-transform"><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg>
          </Link>
        </div>
      </div>

      {/* Secondary actions — the plan lives here now, not the hero */}
      <div className="grid grid-cols-3 gap-3 lg:gap-4">
        <Link to="/planner" className="group bg-panel rounded-2xl p-4 border border-hairline text-left overflow-hidden hover:border-primary/40 transition-all duration-200 hover:shadow-lg hover:shadow-glow-sm active:scale-[0.98]">
          <div className="flex items-start justify-between">
            <div className="w-9 h-9 rounded-xl bg-primary-tint text-primary flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4" /><path d="M8 2v4" /><path d="M3 10h18" /></svg>
            </div>
            {currentPlan && <span className="font-body text-[10px] font-semibold text-primary-text bg-primary-tint px-2 py-0.5 rounded-full">Live</span>}
          </div>
          <p className="font-body text-sm font-bold text-ink mt-3">This Week&apos;s Plan</p>
          <p className="font-body text-[11px] text-ink-muted mt-0.5">{currentPlan ? formatWeekOf(currentPlan.week_of) : 'New plan every Monday'}</p>
        </Link>

        <Link to="/recipes/new" className="group bg-panel rounded-2xl p-4 border border-hairline text-left overflow-hidden hover:border-primary/40 transition-all duration-200 hover:shadow-lg hover:shadow-glow-sm active:scale-[0.98]">
          <div className="w-9 h-9 rounded-xl bg-primary-tint text-primary flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v8" />
              <path d="M8 12h8" />
            </svg>
          </div>
          <p className="font-body text-sm font-bold text-ink mt-3">Add recipe</p>
          <p className="font-body text-[11px] text-ink-muted mt-0.5">Family favorites</p>
        </Link>

        <Link to="/stats" className="group bg-panel rounded-2xl p-4 border border-hairline text-left overflow-hidden hover:border-primary/40 transition-all duration-200 hover:shadow-lg hover:shadow-glow-sm active:scale-[0.98]">
          <div className="w-9 h-9 rounded-xl bg-primary-tint text-primary flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><line x1="6" y1="20" x2="6" y2="14" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="18" y1="20" x2="18" y2="10" /></svg>
          </div>
          <p className="font-body text-sm font-bold text-ink mt-3">Stats</p>
          <p className="font-body text-[11px] text-ink-muted mt-0.5">Family stats</p>
        </Link>
      </div>

      {tonightTitle && (
        <Link to="/tonight" className="block relative bg-panel rounded-2xl border border-hairline overflow-hidden hover:border-mint/40 transition-colors">
          <div className="p-5 lg:p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display text-base lg:text-lg font-semibold text-ink">Tonight&apos;s dinner</h3>
              <span className="font-body text-[11px] text-ink-muted">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</span>
            </div>
            <p className="font-serif text-xl lg:text-2xl text-ink">{tonightTitle}</p>
            {tonightStartBy && <p className="font-body text-[11px] text-ink-muted mt-1">Start by {tonightStartBy}</p>}
            <span className="inline-block mt-3 font-body text-sm font-semibold text-primary-text">See tonight →</span>
          </div>
        </Link>
      )}

      {todayMeals && (
        <div className="relative bg-panel rounded-2xl border border-hairline overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-lime via-mint to-streak" />
          <div className="p-5 pl-6 lg:p-6 lg:pl-7">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-base lg:text-lg font-semibold text-ink">Tonight&apos;s dinner</h3>
              <span className="font-body text-[11px] lg:text-xs text-ink-muted">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
              </span>
            </div>
            <div className="space-y-3 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-4">
              {todayMeals.map((meal, i) => {
                const startBy = getStartByTime(mealTimes[meal.type], meal.prep, meal.cook);
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => meal.recipe && setSelectedRecipe(meal.recipe)}
                    className="w-full flex items-center gap-3 lg:flex-col lg:items-start lg:p-4 lg:bg-surface/50 lg:rounded-xl group text-left rounded-xl p-1 -m-1 lg:m-0 hover:bg-surface lg:hover:bg-surface active:bg-surface transition-colors"
                  >
                    <div className="shrink-0 group-hover:scale-105 transition-transform">
                      <RecipeThumb recipe={meal.recipe} className="w-10 h-10 lg:w-12 lg:h-12" />
                    </div>
                    <div className="flex-1 min-w-0 lg:w-full">
                      <p className="font-body text-sm font-medium text-ink truncate">
                        {meal.name}
                        {meal.label && <span className="text-ink-muted text-xs ml-1.5">({meal.label})</span>}
                      </p>
                      <p className="font-body text-[11px] text-ink-muted capitalize">
                        {meal.type}
                        {startBy && <span className="ml-1.5 text-ink-soft">· Start by {startBy}</span>}
                      </p>
                    </div>
                    <span className="font-body text-[11px] text-ink-muted tabular-nums shrink-0 lg:hidden">
                      {formatMealTime(mealTimes[meal.type])}
                    </span>
                    <span className="hidden lg:block font-body text-[11px] text-ink-muted tabular-nums mt-auto pt-2">
                      {formatMealTime(mealTimes[meal.type])}
                    </span>
                  </button>
                );
              })}
            </div>
            <Link to="/planner" className="inline-block mt-4 font-body text-sm font-semibold text-lime hover:text-lime transition-colors">View full plan →</Link>
          </div>
        </div>
      )}

      {isSunday && (
        <div className="bg-gradient-to-br from-surface to-panel rounded-2xl p-5 lg:p-6 text-ink border border-lime/20 shadow-lg shadow-glow-sm">
          <div className="flex items-start gap-3">
            <span className="text-2xl shrink-0" aria-hidden="true">📊</span>
            <div>
              <p className="font-body text-[10px] text-lime font-semibold mb-1">Weekly recap</p>
              <h3 className="font-display text-base lg:text-lg font-semibold">Week in review</h3>
              <p className="font-body text-sm text-ink/75 mt-1 leading-relaxed">
                Your end-of-week recap is coming soon: top picks, weekly wins, and family highlights.
              </p>
            </div>
          </div>
        </div>
      )}

      <HouseholdFeed
        items={activityItems}
        onRecipeClick={handleFeedRecipeClick}
      />

      {selectedRecipe && (
        <RecipeDetail recipe={selectedRecipe} onClose={() => setSelectedRecipe(null)} />
      )}
    </div>
  );
}
